import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of, catchError, throwError, map } from 'rxjs';
import { TransactionFilter, PagedMerchantTransactionResult } from '../models/merchant-transaction.model';
import { CurrencyNetworkResponse, WalletBalances, WhitelistedWalletType } from '../models/wallet.model';
import {
  CreateMerchantEntityRequest,
  FeeProfile,
  Merchant,
  MerchantDailyAllowance,
  MerchantEntity,
  MerchantRiskGroup,
  Profile,
  RiskProfileDropdownItem,
  ViewMerchant,
} from '../models/merchant.model';
import { PagedAndSortedResultRequestDto, PagedResultDto } from '../models/paged-result.dto';
import type { AcknowledgeTaskDto } from '../models/task.model';
import { AUTH_CONFIG, type AuthConfig } from '@olv/ui-shared/features/auth';
import { AppContextService } from './app-context.service';
import { isMerchantDisabledError, merchantAccessContext } from './merchant-access';
import { WhitelistedWalletSourceEnum } from '../features/dashboard/merchant-whitelisted-wallets/merchant-whitelisted-wallets.models';

// Define interfaces for Whitelisted Wallet
export interface WhitelistedWalletCreateDto {
  merchantId: string;
  WalletAddress: string;
  blockchain: string;
  name: string;
  whitelistedWalletType?: WhitelistedWalletType;
}

export interface WhitelistedWalletValidateBody {
  MerchantId: string;
  NetworkType: number;
  WalletAddress: string;
  WalletName: string;
}

export interface ValidateResponse {
  isValid?: boolean;
  message?: string;
}

// Assuming a response DTO similar to what the backend returns
export interface WhitelistedWalletResponseDto {
  id: string;
  walletAddress: string;
  blockchain: string;
  walletName: string;
  merchantName?: string;
  preparedBy: string;
  date: string; // Or Date object
  withdrawalType: string;
  riskLevel: string;
  status: string;
  documentId?: string;
  documentName?: string;
  comments?: string;
  ownerType?: number;
  riskAssessmentCompleted: boolean;
  canApproveOrReject: boolean;
  source?: WhitelistedWalletSourceEnum;
}

export interface WhitelistedWalletFilterParams extends PagedAndSortedResultRequestDto {
  filter?: string;
  merchantIdFilter?: string;
  networkIdFilter?: string;
  networkTypes?: number[];
  walletStatuses?: number[];
  riskLevels?: number[];
  whitelistedWalletTypes?: number[];
}

export interface MerchantResponse {
  merchants: ViewMerchant[];
  totalCount: number;
  page: number;
  pageSize: number;
  logo?: FileResponse;
}

export interface FileResponse {
  id: string;
  merchantId: string;
  fileName: string;
  contentType: string;
  size: number;
  fileData: string;
}

export interface MerchantFilter {
  searchTerm?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
  status?: number;
  actionRequired?: number;
}

export interface FeeProfilesResponse {
  merchantProfiles: Profile[];
  feeProfiles: FeeProfile[];
}

export interface MerchantUpdateDto {
  name: string;
  code: string;
  merchantProfileId: string;
  merchantFeeProfileId: string;
  riskProfileId?: string | null;
  status: string;
  supervisorEmail: string;
  callbackUrl: string;
  isAssetFundingMerchant?: boolean;
}

export interface RiskProfileDropdownResponse {
  riskProfiles: RiskProfileDropdownItem[];
}

// Slim option shape returned by GET /api/Merchant/risk-profiles (enabled profiles only).
export interface RiskProfileOption {
  id: string;
  name: string;
}

// The merchant GET returns the assigned risk profile as { id, name, groups }; the rest of the UI uses
// profileName and the flattened riskGroups.
type MerchantApiResponse = Omit<Merchant, 'riskProfile' | 'riskGroups'> & {
  riskProfile?: (RiskProfileOption & { groups?: MerchantRiskGroup[] }) | null;
};

export interface MerchantMediaFile {
  id: string;
  merchantId: string;
  contentType: string;
  fileData: string;
  fileName: string | null;
  size: number;
  createdAt: string; // keep as string, parse to Date if needed
}

export interface ToggleSweepingRequestDto {
  totp: string;
}

export interface WalletCheckResponse {
  isOwner: boolean;
}

export interface ClientDetailsResponse {
  clientId: string;
  clientSecret: string;
}

// Interface for the paged response of whitelisted wallets
export type PagedWhitelistedWalletsResultDto = PagedResultDto<WhitelistedWalletResponseDto>;

// HttpErrorResponse.message is a framework string; the real cause lives in the body.
function serverErrorMessage(error: HttpErrorResponse, fallback: string): string {
  const hasText = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0;

  const body = error.error;

  if (hasText(body)) return body;

  const shaped = body as
    | {
        rejectionReason?: string;
        message?: string;
        detail?: string;
        title?: string;
        text?: string;
        errors?: Record<string, string[] | string>;
      }
    | null
    | undefined;

  const modelState = Object.values(shaped?.errors ?? {})
    .flat()
    .filter(hasText);

  return (
    [shaped?.rejectionReason, shaped?.message].find(hasText) ??
    (modelState.length ? modelState.join(' ') : undefined) ??
    [shaped?.detail, shaped?.title, shaped?.text].find(hasText) ??
    fallback
  );
}

/**
 * Query parameter on GET api/Merchant/{merchantId}/balance that makes the backend recompute the balance
 * instead of serving its cached header totals. Kept in one place so it is easy to align with the API.
 */
export const MERCHANT_BALANCE_REFRESH_PARAM = 'refresh';

/** tick changes on every update, so saving the same merchant twice still notifies. */
export interface MerchantUpdate {
  merchantId: string;
  tick: number;
}

@Injectable({
  providedIn: 'root',
})
export class MerchantService {
  private readonly http = inject(HttpClient);

  private readonly cfg = inject<AuthConfig>(AUTH_CONFIG);
  private readonly appCtx = inject(AppContextService);
  private readonly apiUrl = `${this.cfg.baseUrl}/api/Merchant`;
  private readonly mediaUrl = `${this.cfg.baseUrl}/api/MediaFile`;

  isSettlementApp = this.appCtx.getAppName() === 'settlement';
  private readonly whitelistedWalletApiUrl = `${this.cfg.baseUrl}/api/${
    this.isSettlementApp ? 'MerchantWhitelistedWallet' : 'WhitelistedWallet'
  }`;

  merchantUpdatedSignal: WritableSignal<MerchantUpdate | null> = signal<MerchantUpdate | null>(null);
  logoSignal: WritableSignal<string | null> = signal<string | null>(null);
  sidebarOpenSignal: WritableSignal<boolean> = signal(false);
  assetsDrawerOpenSignal: WritableSignal<boolean> = signal(false);
  walletDetailRefreshSignal: WritableSignal<number> = signal(0);
  merchantDetailRefreshSignal: WritableSignal<number> = signal(0);

  //Signal Control Methods
  openSidebar(): void {
    this.sidebarOpenSignal.set(true);
  }

  closeSidebar(): void {
    this.sidebarOpenSignal.set(false);
  }

  toggleSidebar(): void {
    this.sidebarOpenSignal.update((current) => !current);
  }

  triggerMerchantUpdate(merchantId: string): void {
    this.merchantUpdatedSignal.update((prev) => ({ merchantId, tick: (prev?.tick ?? 0) + 1 }));
  }

  triggerWalletDetailRefresh(): void {
    this.walletDetailRefreshSignal.update((v) => v + 1);
  }

  triggerMerchantDetailRefresh(): void {
    this.merchantDetailRefreshSignal.update((v) => v + 1);
  }

  setLogo(base64: string): void {
    this.logoSignal.set(base64);
  }

  /**
   * Get all merchants with optional filtering
   * @param filter Optional filter parameters
   */
  getMerchants(filter?: MerchantFilter): Observable<MerchantResponse> {
    let params = new HttpParams();

    if (filter) {
      // Always include the Filter parameter, even if searchTerm is an empty string
      params = params.set('Filter', filter.searchTerm || '');

      if (filter.pageSize !== undefined) {
        params = params.set('MaxResultCount', filter.pageSize.toString());
      }

      if (filter.page !== undefined && filter.pageSize !== undefined) {
        params = params.set('SkipCount', (filter.page * filter.pageSize).toString());
      }

      if (filter.sortBy) {
        let sorting = filter.sortBy;
        if (filter.sortDirection) {
          sorting += ` ${filter.sortDirection.toUpperCase()}`;
        }
        params = params.set('Sorting', sorting);
      }

      // Add status filter if provided and not 'All'
      if (filter.status !== undefined) {
        params = params.set('Status', filter.status);
      }

      // Add actionRequired filter if provided and not 'All'
      if (filter.actionRequired !== undefined) {
        params = params.set('ActionRequired', filter.actionRequired);
      }
    }

    return this.http.get<any>(this.apiUrl, { params }).pipe(
      map((response) => {
        // Transform the API response items to match our Merchant model
        const transformedMerchants = (response.items || []).map((item: any) => {
          // Generate initials for logo text from name
          const initials = item.name
            .split(' ')
            .map((word: string) => word[0])
            .slice(0, 2)
            .join('')
            .toUpperCase();

          // Transform each merchant item to match our Merchant model
          return {
            id: item.id,
            name: item.name,
            logoText: initials,
            logoClass: 'default-logo', // Default logo class
            isActive: item.isActive,
            isSweepingEnabled: item.isSweepingEnabled,
            pendingActions: item.pendingActions,
            logo: item.logo,
            baseCurrency: item.baseCurrency,
            baseCurrencyDisplayPrecision: item.baseCurrencyDisplayPrecision,
            totalBalanceInBaseCurrency: item.totalBalanceInBaseCurrency,
          } as ViewMerchant;
        });

        // Return the transformed response
        return {
          merchants: transformedMerchants,
          totalCount: response.totalCount || 0,
          page: filter?.page ?? 0,
          pageSize: filter?.pageSize ?? 10,
        } as MerchantResponse;
      }),
      catchError(
        this.handleError<MerchantResponse>('getMerchants', {
          merchants: [],
          totalCount: 0,
          page: filter?.page ?? 0, // Return the requested page/pageSize in the response
          pageSize: filter?.pageSize ?? 10,
        }),
      ),
    );
  }

  /**
   * Get a specific merchant by ID
   * @param id Merchant ID
   */
  // Flattens the API's nested riskProfile.groups into merchant.riskGroups (and the slim riskProfile shape the UI uses).
  // Shared by both merchant fetches so the merchant portal ("my own merchant", no id) gets risk groups too.
  private mapMerchantResponse(merchant: MerchantApiResponse): Merchant {
    return {
      ...merchant,
      riskProfile: merchant.riskProfile
        ? { id: merchant.riskProfile.id, profileName: merchant.riskProfile.name }
        : null,
      riskGroups: merchant.riskProfile?.groups ?? [],
    };
  }

  getMerchantById(id: string): Observable<Merchant> {
    const url = `${this.apiUrl}/${id}`;
    return this.http.get<MerchantApiResponse>(url, { context: merchantAccessContext() }).pipe(
      map((merchant) => this.mapMerchantResponse(merchant)),
      catchError((error) => {
        // Kept as the HttpErrorResponse so the caller can tell a disabled merchant apart from a failed request
        if (isMerchantDisabledError(error)) {
          return throwError(() => error);
        }
        const message = `Failed to fetch merchant with ID ${id}: ${error.message}`;
        console.error(message);
        return throwError(() => new Error(message));
      }),
    );
  }

  getMerchantWithoutId(): Observable<Merchant> {
    const url = `${this.apiUrl}`;
    return this.http.get<MerchantApiResponse>(url).pipe(
      map((merchant) => this.mapMerchantResponse(merchant)),
      catchError((error) => {
        const message = `Failed to fetch merchant: ${error.message}`;
        console.error(message);
        return throwError(() => new Error(message));
      }),
    );
  }

  /**
   * @param refresh true asks the backend to bypass its short-lived header totals cache, for an explicit
   * refresh after an action that changed the balance
   */
  getMerchantBalanceById(id: string, refresh = false): Observable<WalletBalances> {
    const url = `${this.apiUrl}/${id}/balance`;
    const params = refresh ? new HttpParams().set(MERCHANT_BALANCE_REFRESH_PARAM, 'true') : undefined;
    return this.http.get<WalletBalances>(url, { params }).pipe(
      catchError((error) => {
        const message = `Failed to fetch balance for merchant ${id}: ${error.message}`;
        console.error(message);
        return throwError(() => new Error(message));
      }),
    );
  }

  /**
   * Get a specific merchant by ID (alias for getMerchantById)
   * @param id Merchant ID
   */
  getMerchant(id: string): Observable<Merchant> {
    return this.getMerchantById(id);
  }

  /**
   * Fetch all fee-profiles and merchant-profiles
   */
  getFeeProfiles(): Observable<FeeProfilesResponse> {
    const url = `${this.apiUrl}/fee-profiles`;
    return this.http.get<FeeProfilesResponse>(url).pipe(
      // you can transform or tap if needed here
      catchError(
        this.handleError<FeeProfilesResponse>('getFeeProfiles', {
          merchantProfiles: [],
          feeProfiles: [],
        }),
      ),
    );
  }

  getRiskProfilesForDropdown(): Observable<RiskProfileDropdownItem[]> {
    const url = `${this.cfg.baseUrl}/api/RiskProfile`;

    return this.http.get<RiskProfileDropdownResponse | RiskProfileDropdownItem[]>(url).pipe(
      map((response) => (Array.isArray(response) ? response : (response.riskProfiles ?? []))),
      catchError(this.handleError<RiskProfileDropdownItem[]>('getRiskProfilesForDropdown', [])),
    );
  }

  // Enabled risk profiles (Id + Name only) for the merchant assignment dropdown in the Settlement UI.
  // Backed by GET /api/Merchant/risk-profiles, which returns slim options without per-currency balances.
  getAssignableRiskProfiles(): Observable<RiskProfileDropdownItem[]> {
    const url = `${this.apiUrl}/risk-profiles`;

    return this.http.get<RiskProfileOption[]>(url).pipe(
      map((options) => (options ?? []).map((option) => ({ id: option.id, profileName: option.name }))),
      catchError(this.handleError<RiskProfileDropdownItem[]>('getAssignableRiskProfiles', [])),
    );
  }

  // Per-day transaction allowance per risk level, plus the remaining pot for each day.
  // The risk levels are the withdrawal risk groups of the selected profile, so it is required.
  getMerchantDailyAllowance(riskProfileId: string): Observable<MerchantDailyAllowance> {
    const url = `${this.apiUrl}/daily-allowance`;
    const params = new HttpParams().set('riskProfileId', riskProfileId);

    return this.http.get<MerchantDailyAllowance>(url, { params }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to load daily allowance', error.status, error.error);
        return throwError(
          () =>
            new Error(
              serverErrorMessage(error, 'Failed to load resource allocation. Please try again.'),
            ),
        );
      }),
    );
  }

  /**
   * Add a new merchant
   * @param merchant Merchant data
   */
  addMerchant(merchant: Partial<Merchant>): Observable<Merchant> {
    return this.http.post<Merchant>(this.apiUrl, merchant).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to add merchant', error.status, error.error);
        return throwError(
          () => new Error(serverErrorMessage(error, 'Failed to add merchant. Please try again.')),
        );
      }),
    );
  }

  /**
   * Update an existing merchant directly (no approval required)
   * @param merchant Merchant data with ID
   */
  updateMerchant(merchant: Merchant): Observable<Merchant> {
    const url = `${this.apiUrl}/${merchant.id}`;
    const dto: MerchantUpdateDto = {
      name: merchant.name,
      code: merchant.code,
      merchantProfileId: merchant.profile.id,
      merchantFeeProfileId: merchant.feeProfile.id,
      riskProfileId: merchant.riskProfile?.id ?? null,
      supervisorEmail: merchant.supervisorEmail,
      callbackUrl: merchant.callbackUrl,
      isAssetFundingMerchant: merchant.isAssetFundingMerchant ?? false,
      status: merchant.isActive ? 'Active' : 'Inactive',
    };

    return this.http.put<Merchant>(url, dto).pipe(
      catchError((error) => {
        const message = `Failed to update merchant: ${error.message}`;
        return throwError(() => new Error(message));
      }),
    );
  }

  /**
   * Update merchant with only changed fields
   * @param merchantId Merchant ID
   * @param changes Partial update DTO with only changed fields
   */
  updateMerchantPartial(merchantId: string, changes: Partial<MerchantUpdateDto>): Observable<Merchant> {
    const url = `${this.apiUrl}/${merchantId}`;

    return this.http.put<Merchant>(url, changes).pipe(
      catchError((error) => {
        const message = `Failed to update merchant: ${error.message}`;
        return throwError(() => new Error(message));
      }),
    );
  }

  /**
   * Delete a merchant
   * @param id Merchant ID
   */
  deleteMerchant(id: number): Observable<void> {
    const url = `${this.apiUrl}/${id}`;
    return this.http.delete<void>(url).pipe(
      catchError((error) => {
        const message = `Failed to delete merchant: ${error.message}`;
        console.error(message);
        return throwError(() => new Error(message));
      }),
    );
  }

  /**
   * Get merchant statistics
   * @returns Observable with merchant statistics
   */
  getMerchantStats(): Observable<any> {
    const url = `${this.apiUrl}/stats`;
    return this.http.get<any>(url).pipe(catchError(this.handleError<any>('getMerchantStats', {})));
  }

  /**
   * Get transaction history for a specific merchant with pagination
   * @param merchantId Merchant ID
   * @param filter Optional filter parameters
   */
  getMerchantTransactions(
    merchantId: string | undefined,
    isMerchantApp: boolean,
    filter?: TransactionFilter,
  ): Observable<PagedMerchantTransactionResult> {
    const url = isMerchantApp
      ? `${this.cfg.baseUrl}/api/Transaction`
      : `${this.cfg.baseUrl}/api/MerchantTransaction/${merchantId}`;
    let params = new HttpParams();

    if (filter) {
      if (filter.skipCount !== undefined) {
        params = params.set('SkipCount', filter.skipCount.toString());
      }

      if (filter.maxResultCount !== undefined) {
        params = params.set('MaxResultCount', filter.maxResultCount.toString());
      }

      if (filter.filter) {
        params = params.set('Filter', filter.filter);
      }

      if (filter.sorting) {
        params = params.set('Sorting', filter.sorting);
      }

      if (filter.account) {
        params = params.set('Account', filter.account);
      }

      if (filter.actionRequired !== undefined) {
        filter.actionRequired.forEach((action) => (params = params.append('ActionRequired', action.toString())));
      }

      if (filter.transactionType?.length) {
        filter.transactionType.forEach((type) => (params = params.append('TransactionType', type.toString())));
      }

      if (filter.status?.length) {
        filter.status.forEach((status) => (params = params.append('TransactionStatus', status.toString())));
      }

      if (filter.blockchain?.length) {
        filter.blockchain.forEach((network) => (params = params.append('Blockchain', network.toString())));
      }

      if (filter.riskLevel?.length) {
        filter.riskLevel.forEach((level) => (params = params.append('RiskLevel', level.toString())));
      }

      if (filter.exchangeCategories?.length) {
        filter.exchangeCategories.forEach((category) => (params = params.append('ExchangeCategories', category)));
      }

      // Settlement-only: how a rate was set is operator context the merchant portal is never shown, so the
      // filter must not be sent from it even if a caller populates it. Tri-state otherwise — undefined applies
      // no restriction, so false has to be sent explicitly rather than skipped.
      if (!isMerchantApp && filter.exchangeRateOverridden !== undefined) {
        params = params.set('ExchangeRateOverridden', filter.exchangeRateOverridden.toString());
      }
    }

    return this.http.get<PagedMerchantTransactionResult>(url, { params }).pipe(
      map((response) => {
        const transformedItems = response.items.map((item) => ({
          ...item,
          date: new Date(item.date),
        }));

        return {
          ...response,
          items: transformedItems,
        };
      }),
      catchError(
        this.handleError<PagedMerchantTransactionResult>('getMerchantTransactions', {
          totalCount: 0,
          items: [],
        }),
      ),
    );
  }

  /**
   * Fetch Base64 fileData
   */
  getFileData(mediaId: string): Observable<MerchantMediaFile> {
    return this.http.get<MerchantMediaFile>(`${this.mediaUrl}/${mediaId}`).pipe(
      catchError((err) => {
        console.error(`getFileData failed: ${err.message}`);
        return of({} as MerchantMediaFile);
      }),
    );
  }

  /**
   * Update the merchant logo
   * @param merchantId Merchant ID
   * @param file File to upload
   */
  updateMerchantLogo(merchantId: string, file: File): Observable<FileResponse> {
    const url = `${this.mediaUrl}/upload/${merchantId}`;
    const formData = new FormData();
    formData.append('File', file, file.name);

    return this.http
      .post<FileResponse>(url, formData)
      .pipe(catchError(this.handleError<FileResponse>('updateMerchantLogo')));
  }

  /**
   * Update the status of a merchant by ID
   * @param id Merchant ID
   * @param active Current active status
   */
  updateMerchantStatus(id: string, active: boolean): Observable<void> {
    const url = active ? `${this.apiUrl}/${id}/disable` : `${this.apiUrl}/${id}/enable`;
    return this.http.put<void>(url, null).pipe(
      catchError((error) => {
        console.error(`Failed to update status for merchant ${id}: ${error.message}`);
        return throwError(() => error);
      }),
    );
  }

  updateMerchantSweepingStatus(merchantId: string, isSweepingEnabled: boolean, totp: string): Observable<void> {
    const url = isSweepingEnabled
      ? `${this.apiUrl}/${merchantId}/disable-sweeping`
      : `${this.apiUrl}/${merchantId}/enable-sweeping`;

    const body: ToggleSweepingRequestDto = {
      totp: totp,
    };

    return this.http.put<void>(url, body).pipe(
      catchError((error) => {
        const message = `Failed to update sweeping status for merchant ${merchantId}: ${error.message}`;
        console.error(message);
        return throwError(() => error);
      }),
    );
  }

  addWhitelistedWallet(
    walletData: WhitelistedWalletCreateDto,
    totp: string,
    documentFile?: File,
  ): Observable<WhitelistedWalletResponseDto> {
    const formData = new FormData();

    formData.append('merchantId', walletData.merchantId);
    formData.append('WalletAddress', walletData.WalletAddress);
    formData.append('NetworkType', walletData.blockchain);
    formData.append('name', walletData.name);
    formData.append('totp', totp);
    if (walletData.whitelistedWalletType !== undefined) {
      formData.append('WhitelistedWalletType', String(walletData.whitelistedWalletType));
    }
    if (documentFile) {
      formData.append('documentFile', documentFile, documentFile.name);
    }

    return this.http.post<WhitelistedWalletResponseDto>(this.whitelistedWalletApiUrl, formData).pipe(
      catchError((error) => {
        console.error(error);
        return throwError(() => error);
      }),
    );
  }

  validateWhitelistedWallet(walletData: WhitelistedWalletValidateBody): Observable<ValidateResponse | void> {
    const url = `${this.whitelistedWalletApiUrl}/validate`;
    return this.http.post<ValidateResponse | void>(url, walletData).pipe(catchError((err) => throwError(() => err)));
  }

  getWhitelistedWallets(params?: WhitelistedWalletFilterParams): Observable<PagedWhitelistedWalletsResultDto> {
    let httpParams = new HttpParams();
    if (params) {
      if (params.skipCount !== undefined) {
        httpParams = httpParams.set('SkipCount', params.skipCount.toString());
      }
      if (params.maxResultCount !== undefined) {
        httpParams = httpParams.set('MaxResultCount', params.maxResultCount.toString());
      }
      if (params.sorting) {
        httpParams = httpParams.set('Sorting', params.sorting);
      }
      if (params.filter) {
        httpParams = httpParams.set('Filter', params.filter);
      }
      if (params.merchantIdFilter) {
        httpParams = httpParams.set('MerchantIdFilter', params.merchantIdFilter);
      }
      if (params.networkIdFilter) {
        httpParams = httpParams.set('NetworkIdFilter', params.networkIdFilter);
      }
      if (params.networkTypes?.length) {
        params.networkTypes.forEach((val) => (httpParams = httpParams.append('NetworkTypes', val.toString())));
      }
      if (params.walletStatuses?.length) {
        params.walletStatuses.forEach((val) => (httpParams = httpParams.append('WalletStatuses', val.toString())));
      }
      if (params.whitelistedWalletTypes?.length) {
        params.whitelistedWalletTypes.forEach(
          (val) => (httpParams = httpParams.append('WhitelistedWalletTypes', val.toString())),
        );
      }
      if (params.riskLevels?.length) {
        params.riskLevels.forEach((val) => (httpParams = httpParams.append('RiskLevels', val.toString())));
      }
    }

    return this.http
      .get<PagedWhitelistedWalletsResultDto>(this.whitelistedWalletApiUrl, {
        params: httpParams,
      })
      .pipe(
        map((response) => ({
          totalCount: response.totalCount,
          items: response.items.map((item) => ({
            ...item,
            // Optionally normalize here
          })),
        })),
        catchError(
          this.handleError<PagedWhitelistedWalletsResultDto>('getWhitelistedWallets', {
            totalCount: 0,
            items: [],
          }),
        ),
      );
  }

  /**
   * Approve or reject a whitelisted wallet by ID
   * @param id Wallet ID
   * @param action 'approve' or 'reject' or 'revoke'
   */
  updateWhitelistedWalletStatus(
    id: string,
    action: 'approve' | 'reject' | 'revoke',
    comment?: string,
    otp?: string,
  ): Observable<void> {
    const url = `${this.whitelistedWalletApiUrl}/${id}/${action}`;
    const body = {
      comments: comment,
      totp: otp,
    };
    return this.http.put<void>(url, body).pipe(
      catchError((error) => {
        console.error(error);
        return throwError(() => error);
      }),
    );
  }

  acknowledgeMerchantTask(merchantId: string, taskId: string, input: AcknowledgeTaskDto): Observable<void> {
    const url = `${this.apiUrl}/${merchantId}/acknowledge/${taskId}`;
    return this.http.put<void>(url, input).pipe(
      catchError((error) => {
        console.error('Failed to acknowledge merchant task', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Get currency based on network type
   * @param networkType
   * @returns Observable of CurrencyNetworkResponse array
   */
  getCurrencyNetwork(networkType: number): Observable<CurrencyNetworkResponse[]> {
    const onlyStableCoins = !this.isSettlementApp;

    const url = `${this.cfg.baseUrl}/api/Currency/network/${networkType}`;

    return this.http
      .get<CurrencyNetworkResponse[]>(url, {
        params: {
          onlyStableCoins,
        },
      })
      .pipe(catchError(this.handleError<CurrencyNetworkResponse[]>('getCurrencyNetwork', [])));
  }

  getClientDetails(): Observable<ClientDetailsResponse> {
    return this.http.get<ClientDetailsResponse>(`${this.apiUrl}/client-details`).pipe(
      catchError((error) => {
        console.error('Error fetching client details:', error);
        return throwError(() => error);
      }),
    );
  }

  resetClientSecret(totp: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/reset-client-secret`, { totp }).pipe(
      catchError((error) => {
        console.error('Error resetting client secret:', error);
        return throwError(() => error);
      }),
    );
  }

  getMerchantEntities(): Observable<MerchantEntity[]> {
    return this.http
      .get<MerchantEntity[]>(`${this.apiUrl}/merchant-entities`)
      .pipe(catchError(this.handleError<MerchantEntity[]>('getMerchantEntities', [])));
  }

  createMerchantEntity(request: CreateMerchantEntityRequest): Observable<MerchantEntity> {
    return this.http.post<MerchantEntity>(`${this.apiUrl}/merchant-entities`, request).pipe(
      catchError((error) => {
        console.error('Failed to create merchant entity', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Fallback error handler that returns a default value
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed: ${error.message}`);
      // Let the app keep running by returning a default result
      return of(result as T);
    };
  }
}
